import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { MapPin, Check } from "lucide-react";

const PIN_RX = /^[1-9]\d{5}$/;
export const addressError = (v = {}) => (!PIN_RX.test(v.pin || "") ? "Select a valid 6-digit PIN code."
  : !v.state ? "Select the state." : !v.city ? "Select the city / district." : !v.location_id ? "Select the locality / area." : "");

// PIN-code driven address: value = {pin, state, city, area, location_id}; onChange(patch)
export function AddressPicker({ value = {}, onChange, error, testid = "addr" }) {
  const [pinQ, setPinQ] = useState(value.pin || "");
  const [locQ, setLocQ] = useState("");
  const validPin = PIN_RX.test(value.pin || "") ? value.pin : "";
  const sugg = useQuery({ queryKey: ["pin-search", pinQ], enabled: /^\d{2,5}$/.test(pinQ) || (PIN_RX.test(pinQ) && pinQ !== validPin),
    queryFn: async () => (await api.get("/locations/pincodes", { params: { q: pinQ } })).data.items });
  const det = useQuery({ queryKey: ["pin", validPin], enabled: !!validPin, retry: false,
    queryFn: async () => (await api.get(`/locations/pincode/${validPin}`)).data });
  const d = det.data;
  const districts = (d?.districts || []).filter((x) => !value.state || x.state === value.state);
  const locs = (d?.localities || []).filter((l) => (!value.state || l.state === value.state) && (!value.city || l.district === value.city)
    && l.name.toLowerCase().includes(locQ.toLowerCase()));

  useEffect(() => {
    if (!d) return;
    const patch = {};
    if (!value.state && d.states.length === 1) patch.state = d.states[0];
    const st = patch.state || value.state;
    const ds = d.districts.filter((x) => !st || x.state === st);
    if (!value.city && ds.length === 1) patch.city = ds[0].name;
    if (Object.keys(patch).length) onChange(patch);
  }, [d]); // eslint-disable-line react-hooks/exhaustive-deps

  const pickPin = (pin) => { setPinQ(pin); setLocQ(""); onChange({ pin, state: "", city: "", area: "", location_id: "" }); };
  const sel = "mt-1 w-full rounded-md border border-slate-200 bg-white px-2 py-2 text-sm";
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-3 space-y-2" data-field="address" data-testid={`${testid}-picker`}>
      <p className="flex items-center gap-1 text-xs font-semibold text-slate-700"><MapPin className="h-3.5 w-3.5" /> Location <span className="text-rose-600">*</span></p>
      <div>
        <label className="text-[11px] text-slate-500">PIN code</label>
        <input value={pinQ} inputMode="numeric" maxLength={6} placeholder="Type PIN code, e.g. 560034" data-testid={`${testid}-pin-input`}
          onChange={(e) => { const q = e.target.value.replace(/\D/g, ""); setPinQ(q); if (value.pin && q !== value.pin) onChange({ pin: "", state: "", city: "", area: "", location_id: "" }); }}
          className={sel} />
        {!validPin && (sugg.data || []).length > 0 && (
          <div className="mt-1 max-h-40 overflow-y-auto rounded-md border border-slate-200 bg-white" data-testid={`${testid}-pin-options`}>
            {sugg.data.map((p) => (
              <button type="button" key={p.pin} onClick={() => pickPin(p.pin)} data-testid={`${testid}-pin-opt-${p.pin}`}
                className="flex w-full justify-between px-2 py-1.5 text-left text-xs hover:bg-slate-50">
                <span className="font-semibold">{p.pin}</span><span className="text-slate-500 truncate pl-2">{p.district}, {p.state} · {p.localities} areas</span>
              </button>))}
          </div>)}
        {PIN_RX.test(pinQ) && !validPin && sugg.isFetched && !(sugg.data || []).length && <p className="mt-1 text-xs text-rose-600" data-testid={`${testid}-pin-notfound`}>PIN code not found in the India Post directory.</p>}
      </div>
      {d && (
        <div className="grid gap-2 sm:grid-cols-2">
          <div><label className="text-[11px] text-slate-500">State</label>
            <select value={value.state || ""} onChange={(e) => onChange({ state: e.target.value, city: "", area: "", location_id: "" })} className={sel} data-testid={`${testid}-state`}>
              <option value="">Select state</option>{d.states.map((s) => <option key={s} value={s}>{s}</option>)}
            </select></div>
          <div><label className="text-[11px] text-slate-500">City / District</label>
            <select value={value.city || ""} onChange={(e) => onChange({ city: e.target.value, area: "", location_id: "" })} className={sel} data-testid={`${testid}-city`}>
              <option value="">Select city / district</option>{districts.map((x) => <option key={x.name + x.state} value={x.name}>{x.name}</option>)}
            </select></div>
        </div>)}
      {d && (
        <div>
          <label className="text-[11px] text-slate-500">Locality / Area ({d.localities.length} for this PIN)</label>
          <input value={locQ} onChange={(e) => setLocQ(e.target.value)} placeholder="Search locality…" className={sel} data-testid={`${testid}-locality-search`} />
          <div className="mt-1 max-h-36 overflow-y-auto rounded-md border border-slate-200 bg-white" data-testid={`${testid}-locality-options`}>
            {locs.map((l) => (
              <button type="button" key={l.id} onClick={() => onChange({ area: l.name, location_id: l.id, state: l.state, city: l.district })} data-testid={`${testid}-loc-${l.id}`}
                className={`flex w-full items-center justify-between px-2 py-1.5 text-left text-xs hover:bg-slate-50 ${value.location_id === l.id ? "bg-emerald-50 font-semibold text-emerald-800" : ""}`}>
                {l.name}{value.location_id === l.id && <Check className="h-3.5 w-3.5" />}
              </button>))}
            {!locs.length && <p className="px-2 py-1.5 text-xs text-slate-400">No matching locality</p>}
          </div>
        </div>)}
      {value.location_id && <p className="text-xs text-emerald-700" data-testid={`${testid}-selected`}>{value.area}, {value.city}, {value.state} – {value.pin}</p>}
      {error && <p className="text-xs text-rose-600" data-testid={`${testid}-error`}>{error}</p>}
    </div>
  );
}
